import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";

const CATEGORIES = ["Land", "Building", "Vehicle", "Equipment", "Solar", "Generator", "Furniture", "Water Pump", "Livestock"];

export default function Assets() {
  const [assets, setAssets] = useState([]);
  async function refresh() {
    try { const { data } = await api.get("/assets"); setAssets(data); }
    catch { toast.error("Could not load Assets360"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <PageHero
        eyebrow="Assets360"
        title="Asset register"
        subtitle="Register of all homestead assets with value and warranty."
        moduleKind="assets"
      />

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>Asset register</CardTitle><CardDescription>{assets.length} tracked</CardDescription></div>
          <NewAsset onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Purchase</TableHead>
              <TableHead>Value</TableHead><TableHead>Insurance</TableHead><TableHead>Warranty</TableHead><TableHead>Location</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {assets.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell><Badge variant="outline">{a.category}</Badge></TableCell>
                  <TableCell>{fmtDate(a.purchase_date)}</TableCell>
                  <TableCell>{fmt(a.value)}</TableCell>
                  <TableCell>{a.insurance || "—"}</TableCell>
                  <TableCell>{a.warranty || "—"}</TableCell>
                  <TableCell>{a.gps_lat ? (<span className="inline-flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" />{a.gps_lat}, {a.gps_lng}</span>) : "—"}</TableCell>
                </TableRow>
              ))}
              {assets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center subtle py-8">No assets registered yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewAsset({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Building", purchase_date: "", value: 0, insurance: "", warranty: "", maintenance_due: "", gps_lat: "", gps_lng: "", notes: "" });
  async function submit() {
    try {
      await api.post("/assets", { ...form, value: Number(form.value), gps_lat: form.gps_lat === "" ? null : Number(form.gps_lat), gps_lng: form.gps_lng === "" ? null : Number(form.gps_lng) });
      toast.success("Asset added"); setOpen(false); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-asset-btn"><Plus className="h-4 w-4 mr-1" />New asset</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New asset</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Purchase date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
          <div><Label>Value</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
          <div><Label>Insurance</Label><Input value={form.insurance} onChange={(e) => setForm({ ...form, insurance: e.target.value })} /></div>
          <div><Label>Warranty</Label><Input value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} /></div>
          <div><Label>Maintenance due</Label><Input type="date" value={form.maintenance_due} onChange={(e) => setForm({ ...form, maintenance_due: e.target.value })} /></div>
          <div><Label>GPS latitude</Label><Input value={form.gps_lat} onChange={(e) => setForm({ ...form, gps_lat: e.target.value })} /></div>
          <div><Label>GPS longitude</Label><Input value={form.gps_lng} onChange={(e) => setForm({ ...form, gps_lng: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
